import { EyeOff, Eye } from "lucide-react";
import Navbar from "../../components/layout/Navbar";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../../components/Button";
import { useState, FormEvent, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  logout,
  selectCurrentUser,
  selectToken,
  setCredentials,
} from "../../features/authSlice";
import { GoogleLoginButton } from "../../components/GoogleAuth/GoogleAuth";
import { toast } from "sonner";
import supabase from "../../helper/supabaseClient";
import { getSites } from "../StorePages/UserStores";

interface Site {
  slug: string;
  [key: string]: any;
}

interface UserData {
  user: {
    sites: Site[];
    [key: string]: any;
  };
  access: string;
  refresh: string;
}

export const loginRedirectLogic = (
  navigate: (x: string) => void,
  userData: UserData
): void => {
  // Safely get search parameters
  let redirectUrl = "";
  let slug = "";

  const pendingName = localStorage.getItem("pendingStoreName");

  localStorage.removeItem("pendingStoreName");
  
  try {
    const searchParams = new URLSearchParams(window.location.search);
    redirectUrl = searchParams.get("redirect") || "";
    slug = searchParams.get("slug") || "";
  } catch (urlError) {
    console.warn("Error parsing URL search parameters:", urlError);
    // Continue without redirect parameters
  }

  // Handle redirect logic
  if (redirectUrl && redirectUrl.trim()) {
    try {
      const redirectUrlObj = new URL(redirectUrl);
      const isPreviewEnv = redirectUrlObj.hostname.startsWith("pr");

      const slugs: (string | null)[] = userData.user.sites
        .map((site: Site): string | null => {
          // Safely extract slug, handle cases where site might be null/undefined
          return site && typeof site === "object" ? site.slug : null;
        })
        .filter(Boolean); // Remove null/undefined values

      if (isPreviewEnv || (slug && slugs.includes(slug))) {
        if (slug) {
          try {
            redirectUrlObj.searchParams.set("slug", slug);
            redirectUrlObj.searchParams.set("access_token", userData.access);
            redirectUrlObj.searchParams.set("refresh_token", userData.refresh);
            window.location.href = redirectUrlObj.toString();
            return;
          } catch (redirectError) {
            console.error("Error setting redirect parameters:", redirectError);
            // Fall through to default navigation
          }
        }
      }
    } catch (urlError) {
      console.error("Error parsing redirect URL:", urlError);
      // Invalid redirect URL, continue with default navigation
    }
  }

  if (userData.user.sites.length < 1) {
    navigate(`/add-store${`?name=${pendingName ? encodeURIComponent(pendingName) : ""}`}`);
  } else {
    navigate(`/my-stores`);
  }
};

const Login = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const token = useSelector(selectToken);
  const currentUser = useSelector(selectCurrentUser);
  // const [login, { isLoading }] = useLoginMutation();
  const [isLoading, setIsLoading] = useState(false);

  const [showPassword, setShowPassword] = useState(false);

  const [formData, setFormData] = useState({
    email: "",
    password: "",
    rememberMe: false,
  });
  const [isValidatingSession, setIsValidatingSession] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const validateAndRedirect = async () => {
      if (!token || !currentUser) return;
      setIsValidatingSession(true);

      const { data: sessionData } = await supabase.auth.getSession();
      if (cancelled) return;

      const candidateToken = sessionData.session?.access_token || token;
      const { data: userData, error: userError } = await supabase.auth.getUser(
        candidateToken
      );
      if (cancelled) return;

      if (userError || !userData.user) {
        dispatch(logout());
        setIsValidatingSession(false);
        return;
      }

      const userSites = await getSites(userData.user.id);
      if (cancelled) return;

      const verifiedUser = {
        ...currentUser,
        id: userData.user.id,
        email: userData.user.email ?? currentUser.email ?? "",
        first_name: userData.user.user_metadata.first_name ?? "",
        last_name: userData.user.user_metadata.last_name ?? "",
        last_login: new Date().toISOString(),
        sites: userSites.data || currentUser.sites || [],
      };

      loginRedirectLogic(navigate, {
        user: verifiedUser as any,
        access: candidateToken,
        refresh: sessionData.session?.refresh_token || "",
      } as any);
    };

    validateAndRedirect().finally(() => {
      if (!cancelled) setIsValidatingSession(false);
    });

    return () => {
      cancelled = true;
    };
  }, [currentUser, dispatch, navigate, token]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: formData.email,
        password: formData.password,
      });

      const userSites = await getSites(data?.user?.id!);
      if (error) {
        toast.error(error.message);
        return;
      }

      if (data?.session && data.user) {
        const mappedUser = {
          id: data.user.id,
          email: data.user.email ?? "", // force string
          first_name: data.user.user_metadata.first_name ?? "",
          last_name: data.user.user_metadata.last_name ?? "",
          last_login: new Date().toISOString(),
          sites: userSites.data || [], // TODO: fetch separately
        };

        dispatch(
          setCredentials({
            user: mappedUser,
            token: data.session.access_token,
            refresh_token: data.session.refresh_token,
            rememberMe: formData.rememberMe,
          })
        );

        loginRedirectLogic(navigate, {
          user: mappedUser,
          access: data.session.access_token,
          refresh: data.session.refresh_token,
        } as any);
      }
    } catch (err) {
      console.error("Login failed:", err);
      toast.error("Unexpected error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <Navbar />
      <div className="p-5 sm:p-14 flex items-center justify-center landing-bg bg-cover bg-no-repeat bg-center">
        <div className="w-full md:w-fit flex items-center justify-center flex-col bg-white">
          <div className=" sm:p-10 rounded-2xl sm:border border-[#7E57C233]  w-[100%] sm:w-[80%] md:w-fit flex items-center justify-between gap-20 ">
            <div className="flex flex-col gap-3 items-start justify-start w-[500px]">
              <div>
                <h2 className="">Welcome Back!</h2>
                <span>
                  Don't have an account?{" "}
                  <Link to="/signup">
                    <span className=" underline text-[#7E57C2] font-medium">
                      Sign up
                    </span>
                  </Link>
                </span>
              </div>

              <div className="flex flex-col items-center justify-center mt-2 w-full">
                <GoogleLoginButton />
              </div>
              <p className="text-center w-full -mb-2">OR</p>

              <form
                className="flex flex-col items-start justify-center gap-8 w-full"
                onSubmit={handleSubmit}
              >
                <div className="flex items-start justify-center gap-6 flex-col w-full">
                  <div className="flex items-start justify-center flex-col gap-1.5 w-full">
                    <label htmlFor="email" className="text-[#666666CC]">
                      Email
                    </label>
                    <input
                      type="email"
                      placeholder="Enter your email"
                      name="email"
                      id="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="h-11 w-full border border-[#66666659] rounded-lg outline-0 px-3"
                      required
                    />
                  </div>
                  <div className="flex items-start justify-center flex-col gap-1.5 w-full">
                    <div className="flex items-center justify-between w-full">
                      <label htmlFor="password" className="text-[#666666CC]">
                        Password
                      </label>
                    </div>
                    <div className="w-full relative">
                      <button
                        type="button"
                        className="absolute right-2 top-1/2 -translate-y-1/2  text-sm"
                        onClick={() => setShowPassword((prev) => !prev)}
                        tabIndex={-1}
                        aria-label={
                          showPassword ? "Hide password" : "Show password"
                        }
                      >
                        {showPassword ? <EyeOff /> : <Eye />}
                      </button>
                      <input
                        type={showPassword ? "text" : "password"}
                        placeholder="Enter password"
                        name="password"
                        id="password"
                        value={formData.password}
                        onChange={handleChange}
                        className="h-11 w-full border border-[#66666659] rounded-lg outline-0 px-3"
                        required
                      />
                    </div>
                  </div>
                  <div className=" flex items-center justify-between w-full -mt-3">
                    <div className="flex items-center justify-center gap-2 outline-0">
                      <input
                        type="checkbox"
                        name="rememberMe"
                        id="rememberMe"
                        checked={formData.rememberMe}
                        onChange={handleChange}
                        className=" accent-[#7E57C2]"
                      />
                      <span>Remember Me</span>
                    </div>
                    <Link to="/reset-password" className="text-[#7E57C2]">
                      Forgot Password?
                    </Link>
                  </div>
                </div>
                <div className="w-full flex items-center justify-between">
                  <Link
                    to="/signup"
                    className="underline text-[#7E57C2] font-medium hidden sm:block"
                  >
                    <span>Sign Up instead</span>
                  </Link>
                  <Button
                    text={
                      isValidatingSession
                        ? "Checking session..."
                        : isLoading
                          ? "Logging In..."
                          : "Log in"
                    }
                    disabled={isLoading || isValidatingSession}
                    className="w-full sm:w-fit"
                  />
                </div>
              </form>
            </div>
            <img
              src="/assets/Jellosite-Logo-09.jpg"
              alt=""
              className="w-[250px] hidden md:block"
            />
          </div>
          <ul className="text-[#666666] md:w-full hidden sm:flex gap-3.5 sm:w-[80%]  items-center justify-end mt-1 ">
            <Link to="">
              <li>Help</li>
            </Link>
            <Link to="/privacy_policy">
              <li>Privacy</li>
            </Link>
            <Link to="/terms_conditions">
              <li>Terms</li>
            </Link>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default Login;
